import React, { useState, useCallback, useMemo, useEffect } from 'react'

import { EditOutlined, CheckOutlined, RedoOutlined } from '@ant-design/icons'
import ArticlePrompt from '@/components/ArticlePrompt'
import ModeSelect from '@/components/form/ModeSelect'
import classnames from 'classnames'
import Image from 'next/image'
import { Modal, Form, Input, Button } from 'antd'

import tryJson from '@/utils/tryJson'
import useReducers from '@/store/long-creation'
import topup from '@/utils/topup'

import useCheckLongCreationPending from '@/hooks/useCheckLongCreationPending'
import styles from './TitleInput.module.scss'
import commonStyles from '../../long-creation.module.scss'
import sendIcon from '@/assets/send.png'

export default React.memo(function TitleInput() {
  const [state, actions] = useReducers()
  const [loading, setLoading] = useState(false)
  const [modifyModalVisible, setModifyModalVisible] = useState(false)
  const [form] = Form.useForm()
  const [titleForm] = Form.useForm()

  useCheckLongCreationPending('TITLE')

  const messages = useMemo(() => {
    return state.messages[state.active] || []
  }, [state.messages, state.active])

  const submit = useCallback(async ({ content, model }) => {
    setLoading(true)
    try {
      const res = await actions.createTitle({
        chatId: state.active,
        content: content,
        extra: { model }
      })

      if (res.error && res.payload?.code === '1001') {
        topup(res.payload?.message)
      }

      setLoading(false)
    } catch {
      setLoading(false)
    }
  }, [state.active])

  const reCreateTitle = useCallback(async () => {
    setLoading(true)
    try {
      await actions.reCreateTitle(state.active)
      setLoading(false)
    } catch {
      setLoading(false)
    }
  }, [state.active])

  const summary = useMemo(() => {
    const message = messages?.find(item => item.type === 'SUMMARY' && item.role === 'user')
    if (!message) {
      return null
    }

    return (
      <div className={classnames(commonStyles.message, commonStyles.user)}>
        <div className={styles.header}>
          {message?.extra?.model || 'gpt-3.5'}
        </div>
        { message.content }
      </div>
    )
  }, [messages])

  const userTitle = useMemo(() => {
    const message = messages?.find(item => item.type === 'TITLE' && item.role === 'user')
    if (!message) {
      return null
    }

    return (
      <div className={classnames(commonStyles.message, commonStyles.user)}>
        <h3>已选标题</h3>
        { message.content }
      </div>
    )
  }, [messages])

  const createOutline = useCallback((title) => {
    return actions.createOutline({
      chatId: state.active,
      title
    })
  }, [state.active])

  const modifyTitle = useCallback(title => {
    setModifyModalVisible(true)
    titleForm.setFieldsValue({
      title: title
    })
  }, [])

  const assistantTitle = useMemo(() => {
    const message = messages?.find(item => item.type === 'TITLE' && item.role === 'assistant')
    const userTitle = messages?.some(item => item.type === 'TITLE' && item.role === 'user')

    if (!message) {
      return null
    }

    if (message.status === 'REMOTE_ERROR') {
      return (
        <div className={classnames(commonStyles.message, commonStyles.assistant)}>
          <div className={styles.error}>
            标题生成失败，请重试 <Button loading={loading} type="primary" size="small" onClick={reCreateTitle}><RedoOutlined/>重新生成</Button>
          </div>
        </div>
      )
    }

    if (message.status === 'PENDING') {
      return (
        <div className={classnames(commonStyles.message, commonStyles.assistant)}>
          正在生成标题...
        </div>
      )
    }

    const titles = tryJson(message.content)

    return (
      <div className={classnames(commonStyles.message, commonStyles.assistant)}>
        {
          titles.map((title, index) => {
            return (
              <div key={index} className={styles.title}>
                <h3>推荐标题{index + 1}</h3>
                <span>
                  {title}
                </span>

                {
                  !userTitle &&
                    <>
                      <Button type="link" size="small" onClick={() => modifyTitle(title)}>
                        <EditOutlined />修改标题
                      </Button>
                      <Button type="primary" size="small" onClick={() => createOutline(title)}>
                        <CheckOutlined />使用此标题
                      </Button>
                    </>
                }
              </div>
            )
          })
        }
      </div>
    )
  }, [messages, createOutline, modifyTitle, reCreateTitle, loading])

  const confirmModifyTitle = useCallback(async () => {
    try {
      const values = await titleForm.validateFields()
      setLoading(true)
      await createOutline(values.title)
      setLoading(false)
      setModifyModalVisible(false)
    } catch {
      setLoading(false)
    }
  }, [state.active, createOutline])

  if (summary) {
    return (
      <div className={styles.messages}>
        {summary}
        {assistantTitle}
        {userTitle}

        <Modal
          visible={modifyModalVisible}
          title="修改并使用标题"
          onCancel={() => setModifyModalVisible(false)}
          onOk={confirmModifyTitle}
          okText="确认"
          cancelText="取消"
          confirmLoading={loading}
        >
          <Form form={titleForm}>
            <Form.Item name="title" rules={[{ required: true, message: '请填写标题'}]}>
              <Input placeholder="请输入标题" onPressEnter={confirmModifyTitle}/>
            </Form.Item>
          </Form>
        </Modal>
      </div>
    )
  }

  return (
    <ArticlePrompt
      extra={
        <Form.Item name="model">
          <ModeSelect></ModeSelect>
        </Form.Item>
      }
      form={form}
      onSubmit={submit}
    >
      <Form.Item
        className={styles.input}
        name="content"
        rules={[{
          required: true, message: '请输入文章描述'
        }, {
          min: 10,
          message: '请至少输入10个字'
        }]}>
        <Input.TextArea maxLength={1000} showCount placeholder={`请描述你想创建的文章\n如：气候变化对全球农业生产的影响，包含目前主要面临的挑战与可能的主要对策。具备较好的独创性和新意。`} autoSize={false}/>
      </Form.Item>
    </ArticlePrompt>
  )
})
//   <Button className={styles.submit} type="primary" loading={loading} size="middle" onClick={submit}>
//     <Image alt="" height={30} src={sendIcon}/>
//   </Button>
// </Form>
